import { gql, useLazyQuery } from '@apollo/client';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import { convertFromRaw } from 'draft-js';
import NextLink from 'next/link';
import * as React from 'react';

import { event_join_clicked, event_joined, event_recording_viewed } from '@/analytics';
import { DialInInfo } from '@/components/DialInInfo';
import { ReadOnly, ReadOnlyValue } from '@/components/ReadOnly';
import { Redraft } from '@/components/Redraft';
import { VenueFragment } from '@/fragments/graphql-types/VenueFragment';
import { EventInteraction } from '@/graphql-types/globalTypes';
import { useTrackEventInteraction } from '@/hooks/useTrackEventInteraction';
import { Prose } from '@/imports/ui/chakra/components/Prose';
import { Semibold } from '@/imports/ui/chakra/components/Semibold';
import { ExternalLinkIcon } from '@/imports/ui/chakra/feather';
import { isVideoFileALink } from '@/utilities';

const INTUTO_COURSE_AUTO_ENROL_URL_QUERY = gql`
  query ModalToJoinExternalEvent_IntutoCourseAutoEnrolUrl($eventId: String!) {
    intutoCourseAutoEnrolUrl(eventId: $eventId)
  }
`;

type IntutoCourseAutoEnrolUrlQuery = {
  intutoCourseAutoEnrolUrl?: string | null;
};

export const ModalToJoinExternalEvent: React.FC<{
  onClose: () => void;
  isOpen: boolean;
  venue: VenueFragment | null;
  isCurrentUserSpeaker: boolean;
  simpleEventOrConferenceId: string;
  meta:
  | { type: 'simple_event'; eventId: string; name: string }
  | { type: 'session'; sessionId: string; name: string }
  | { type: 'booth'; boothId: string; name: string }
  | { type: 'agoraMeet'; meetId: string; name: string };
  isJoiningWithSpecialPermissions: boolean;
  isFuture: boolean;
  isAgoraMeet?: boolean;
  /** When true, never route to /meet — course/workshop uses external hosting only */
  isCourseWorkshop?: boolean;
  meetId?: string;
  isPast: boolean;
  hasCanonicalVideo: boolean;
}> = (props) => {
  const { trackEventInteraction } = useTrackEventInteraction({
    eventMetadata:
      'eventId' in props.meta
        ? { simpleEventId: props.meta.eventId }
        : 'sessionId' in props.meta
          ? { conferenceSessionId: props.meta.sessionId }
          : props.meta.type === 'booth'
            ? { conferenceBoothId: props.meta.boothId }
            : 'meetId' in props.meta
              ? { simpleEventId: props.simpleEventOrConferenceId }
              : { simpleEventId: props.simpleEventOrConferenceId }

  });

  const isAgoraMeetResolved = Boolean(
    !props.isCourseWorkshop &&
      (props.isAgoraMeet ||
        (props.venue &&
          props.venue.online &&
          props.venue.online.__typename === 'EventVenue_TradewingPlatform' &&
          props.venue.online.stream &&
          props.venue.online.stream.__typename ===
            'EventVenue_TradewingPlatform_Stream_Agora_Video_Call'))
  );

  const resolvedMeetId = (
    'meetId' in props.meta ? props.meta.meetId : undefined
  ) || props.meetId || (
      props.venue &&
        props.venue.online &&
        props.venue.online.__typename === 'EventVenue_TradewingPlatform'
        ? props.venue.online.agoraMeetId
        : undefined
    );
  const prerecordedExternalUrl =
    props.venue?.online?.__typename === 'EventVenue_TradewingPlatform' &&
    props.venue.online.stream?.__typename ===
      'EventVenue_TradewingPlatform_Stream_Prerecorded'
      ? props.venue.online.stream.videoFile?._id
      : undefined;
  const externalPlatformUrl =
    props.venue?.online?.__typename === 'EventVenue_ExternalPlatform'
      ? props.venue.online.url
      : undefined;
  const externalPlatformName =
    props.venue?.online?.__typename === 'EventVenue_ExternalPlatform'
      ? props.venue.online.externalPlatformName
      : undefined;

  const [fetchIntutoAutoEnrolUrl, { loading: isFetchingIntutoAutoEnrolUrl }] =
    useLazyQuery<IntutoCourseAutoEnrolUrlQuery>(
      INTUTO_COURSE_AUTO_ENROL_URL_QUERY,
      { fetchPolicy: 'network-only' }
    );

  /**
   * Intuto courses (open registration): instead of sending the member
   * straight to the Intuto course link, first fetch a single-use AutoEnrol
   * URL (valid 5 minutes). Intuto enrols the member and then redirects them
   * to the course page. The server returns null for ticketed courses / when
   * AutoEnrol is not configured, in which case we fall back to the plain
   * external link (existing behaviour).
   */
  const handleJoinOnExternalPlatform = async () => {
    trackJoinOnTradewingClick(
      externalPlatformUrl ?? '',
      `Join on ${externalPlatformName ?? 'an External Platform'}`,
      'external_platform'
    );
    trackEventInteraction(EventInteraction.CLICKED_EXTERNAL_EVENT_LINK);

    if (!props.isCourseWorkshop || !('eventId' in props.meta)) {
      window.open(externalPlatformUrl ?? undefined, '_blank', 'noopener');
      return;
    }

    // Open the tab synchronously so popup blockers don't reject it while
    // the (short-lived) token is being generated.
    const newWindow = window.open('', '_blank');
    try {
      const result = await fetchIntutoAutoEnrolUrl({
        variables: { eventId: props.meta.eventId },
      });
      const targetUrl =
        result.data?.intutoCourseAutoEnrolUrl ?? externalPlatformUrl;
      if (newWindow) {
        newWindow.location.href = targetUrl ?? '';
      } else if (targetUrl) {
        window.open(targetUrl, '_blank', 'noopener');
      }
    } catch {
      if (newWindow) {
        newWindow.location.href = externalPlatformUrl ?? '';
      } else if (externalPlatformUrl) {
        window.open(externalPlatformUrl, '_blank', 'noopener');
      }
    }
  };

  const getTargetHref = () => {
    if ('sessionId' in props.meta) {
      return `/events/conferences/${props.simpleEventOrConferenceId}/sessions/${props.meta.sessionId}`;
    }
    if ('boothId' in props.meta) {
      return `/events/conferences/${props.simpleEventOrConferenceId}/booth/${props.meta.boothId}`;
    }
    if ('eventId' in props.meta) {
      // For simple events that are Agora meets, route to the base meet page first
      // so that AgoraMeetPage can handle lobby/recording logic and then deep-link
      // into the specific meet if needed.
      if (isAgoraMeetResolved) {
        return `/events/${props.simpleEventOrConferenceId}/meet`;
      }
      return `/virtualEvent/${props.simpleEventOrConferenceId}`;
    }
    if ('meetId' in props.meta) {
      if (resolvedMeetId) {
        return `/events/${props.simpleEventOrConferenceId}/meet/${resolvedMeetId}`;
      }
    }
    return `/virtualEvent/${props.simpleEventOrConferenceId}`;
  };

  const trackJoinOnTradewingClick = (
    href: string,
    ctaText: string,
    joinMode: 'tradewing' | 'external_platform' | 'prerecorded'
  ) => {
    let entityId = props.simpleEventOrConferenceId;
    let entityType = 'event';
    let eventType = 'simple';
    let eventSubeventType: string | undefined;

    if ('sessionId' in props.meta) {
      entityId = props.meta.sessionId;
      entityType = 'conference_session';
      eventType = 'conference';
      eventSubeventType = 'session';
    } else if ('boothId' in props.meta) {
      entityId = props.meta.boothId;
      entityType = 'conference_booth';
      eventType = 'conference';
      eventSubeventType = 'booth';
    }

    event_join_clicked({
      entity_id: entityId,
      entity_type: entityType,
      event_id: props.simpleEventOrConferenceId,
      event_type: eventType,
      event_name: props.meta.name,
      event_role: props.isCurrentUserSpeaker
        ? 'speaker'
        : props.isJoiningWithSpecialPermissions
          ? 'admin'
          : 'participant',
      event_subevent_type: eventSubeventType,
      event_join_target_path: href,
      event_join_cta_text: ctaText,
      event_join_source: 'modal_join_cta',
      event_join_surface: 'event_join_modal',
      event_join_mode: joinMode,
      page_section: 'events',
    });

    const sharedJoinProps = {
      entity_id: entityId,
      entity_type: entityType,
      event_id: props.simpleEventOrConferenceId,
      event_type: eventType,
      event_name: props.meta.name,
      event_role: props.isCurrentUserSpeaker
        ? 'speaker'
        : props.isJoiningWithSpecialPermissions
          ? 'admin'
          : 'participant',
      event_subevent_type: eventSubeventType,
      event_join_mode: joinMode,
    };

    if (joinMode === 'external_platform') {
      // Event user journey: member left Tradewing to join Zoom/external (no Agora webhook)
      event_joined(sharedJoinProps);
    }

    if (joinMode === 'prerecorded') {
      // Event user journey: member opened a recording from the join modal
      event_recording_viewed({
        ...sharedJoinProps,
        entity_type: 'event_recording',
        recording_source:
          ctaText === 'Watch on an External Platform'
            ? 'prerecorded_external'
            : 'published_recording',
      });
    }
  };


  const getVenueMessage = () => {

    // No venue data at all
    if (!props.venue) {
      return (
        <Text>
          This {{
            simple_event: 'event',
            session: 'session',
            booth: 'booth',
            agoraMeet: 'meet'
          }[props.meta.type]} will be available at the scheduled time.
        </Text>
      );
    }

    // Has venue but missing online details
    if (!props.venue.online) {
      return (
        <Text>
          This {{
            simple_event: 'event',
            session: 'session',
            booth: 'booth',
            agoraMeet: 'meet'
          }[props.meta.type]} will be hosted on Tradewing.
        </Text>
      );
    }

    // Default to null so other conditions in the component can handle it
    return null;
  };

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      isCentered
      scrollBehavior="inside"
      size="lg"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {{
            simple_event: 'Event',
            session: 'Session',
            booth: 'Booth',
            agoraMeet: 'Meet'
          }[props.meta.type]}{' '}
          Details
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="4">
            {props.isJoiningWithSpecialPermissions && (
              <Alert colorScheme="gray">
                <AlertIcon />
                {props.isFuture ? (
                  <AlertDescription>
                    <Semibold>
                      As{' '}
                      {props.isCurrentUserSpeaker
                        ? 'a speaker'
                        : 'an event admin'}
                      , you are able to preview this event early.
                    </Semibold>{' '}
                    Regular attendees can only join during the scheduled event
                    time.
                  </AlertDescription>
                ) : props.isPast ? (
                  <AlertDescription>
                    <Semibold>This event has ended.</Semibold> As{' '}
                    {props.isCurrentUserSpeaker
                      ? 'a speaker'
                      : 'an event admin'}
                    , you have permission to view the event page.
                  </AlertDescription>
                ) : (
                  <AlertDescription>
                    As{' '}
                    {props.isCurrentUserSpeaker
                      ? 'a speaker'
                      : 'an event admin'}
                    , you have permission to view the event page.
                  </AlertDescription>
                )}
              </Alert>
            )}

            {/* Display default venue message if other conditions don't apply */}
            {getVenueMessage() || (
              props.isPast && props.hasCanonicalVideo ? (
                <Text>
                  This{' '}
                  {{
                    simple_event: `event's`,
                    session: `session's`,
                    booth: `booth's`,
                    agoraMeet: `meet's`
                  }[props.meta.type]}{' '}
                  recording is available on <Semibold>Tradewing</Semibold>.
                </Text>
              ) : props.venue && props.venue.online ? (
                <Text>
                  This{' '}
                  {{
                    simple_event: 'event',
                    session: 'session',
                    booth: 'booth',
                    agoraMeet: 'meet'
                  }[props.meta.type]}{' '}
                  is hosted on{' '}
                  <Semibold>
                    {props.venue.online.__typename ===
                      'EventVenue_TradewingPlatform'
                      ? 'Tradewing'
                      : props.venue.online.externalPlatformName
                        ? props.venue.online.externalPlatformName
                        : 'an external platform'}
                  </Semibold>
                  .
                </Text>
              ) : null
            )}

            {/* {!props.isPast &&
  props.meta.type !== 'agoraMeet' &&
  props.venue.online &&
  props.venue.online.__typename === 'EventVenue_TradewingPlatform' &&
  props.venue.online.stream.__typename ===
    'EventVenue_TradewingPlatform_Stream_Live' &&
  props.venue.online.stream.virtualEvent &&
  props.venue.online.stream.virtualEvent.dialInEnabled &&
  props.venue.online.stream.virtualEvent.dialInPhoneNumber && (
    <Box bg="gray.50" p="4" borderRadius="lg">
      <DialInInfo
        eventName={props.meta.name}
        dialInCode={props.venue.online.stream.virtualEvent.dialInCode}
        listenerDialInCode={props.venue.online.stream.virtualEvent.listenerDialInCode}
        dialInPhoneNumber={props.venue.online.stream.virtualEvent.dialInPhoneNumber}
        isCurrentUserSpeaker={props.isCurrentUserSpeaker}
        isParticipationAllowed={props.venue.online.stream.virtualEvent.maxParticipants > 0}
      />
    </Box>
  )} */}

            {props.venue && props.venue.online && !props.isPast &&
              props.venue.online.__typename === 'EventVenue_ExternalPlatform' &&
              props.venue.online.additionalDetails &&
              convertFromRaw(
                props.venue.online.additionalDetails
              ).getPlainText().length > 0 && (
                <Box bg="gray.50" p="4" borderRadius="lg">
                  <ReadOnly>
                    <ReadOnlyValue as={Prose}>
                      <Redraft
                        rawDraftContentState={
                          props.venue.online.additionalDetails
                        }
                      />
                    </ReadOnlyValue>
                  </ReadOnly>
                </Box>
              )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            colorScheme="gray"
            onClick={props.onClose}
            mr="2"
          >
            Close
          </Button>

          {props.venue && props.venue.online &&
            props.venue.online.__typename === 'EventVenue_TradewingPlatform' &&
            props.venue.online.stream &&
            props.venue.online.stream.__typename ===
            'EventVenue_TradewingPlatform_Stream_Prerecorded' &&
            props.venue.online.stream.videoFile &&
            isVideoFileALink(props.venue.online.stream.videoFile) ? (
            <NextLink
              href={props.venue.online.stream.videoFile._id}
              target="_blank"
              onClick={() =>
                trackJoinOnTradewingClick(
                  prerecordedExternalUrl ?? '',
                  'Watch on an External Platform',
                  'prerecorded'
                )
              }
            >
              <Button rightIcon={<ExternalLinkIcon />}>
                Watch on an External Platform
              </Button>
            </NextLink>
          ) : props.venue && props.venue.online &&
            props.venue.online.__typename === 'EventVenue_TradewingPlatform' ? (
            ['simple_event', 'session', 'booth', 'agoraMeet'].includes(props.meta.type) ? (
              <Button
                rightIcon={<ExternalLinkIcon />}
                onClick={() => {
                  const href = getTargetHref();
                  trackJoinOnTradewingClick(
                    href,
                    'Join on Tradewing',
                    'tradewing'
                  );
                  if ('sessionId' in props.meta) {
                    const router = require('next/router').default;
                    router.push(href);
                  } else {
                    window.open(href, '_blank');
                  }
                }}
              >
                Join on Tradewing
              </Button>
            ) : null
          ) : props.isPast && props.hasCanonicalVideo ? (
            ['simple_event', 'session', 'booth', 'agoraMeet'].includes(props.meta.type) ? (
              <Button
                rightIcon={<ExternalLinkIcon />}
                onClick={() => {
                  const href = getTargetHref();
                  trackJoinOnTradewingClick(
                    href,
                    'Watch on Tradewing',
                    'prerecorded'
                  );
                  if ('sessionId' in props.meta) {
                    const router = require('next/router').default;
                    router.push(href);
                  } else {
                    window.open(href, '_blank');
                  }
                }}
              >
                Watch on Tradewing
              </Button>
            ) : null
          ) : props.venue && props.venue.online &&
            props.venue.online.__typename === 'EventVenue_ExternalPlatform' &&
            props.venue.online.url ? (
            <Button
              rightIcon={<ExternalLinkIcon />}
              isLoading={isFetchingIntutoAutoEnrolUrl}
              onClick={handleJoinOnExternalPlatform}
            >
              Join on{' '}
              {props.venue.online.externalPlatformName ??
                'an External Platform'}
            </Button>
          ) : (
            <NextLink
              href={getTargetHref()}
              target="_blank"
              onClick={() =>
                trackJoinOnTradewingClick(
                  getTargetHref(),
                  props.isPast && props.hasCanonicalVideo
                    ? 'Watch on Tradewing'
                    : 'Join on Tradewing',
                  props.isPast && props.hasCanonicalVideo
                    ? 'prerecorded'
                    : 'tradewing'
                )
              }
            >
              <Button rightIcon={<ExternalLinkIcon />}>
                {props.isPast && props.hasCanonicalVideo
                  ? 'Watch on Tradewing'
                  : 'Join on Tradewing'}
              </Button>
            </NextLink>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
